import React from 'react';
import { View, Text } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  Compass,
  MapPin,
  Eye,
  Sparkles,
  X,
  RotateCcw,
} from 'lucide-react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';

interface DealflowEmptyStateProps {
  /** When true, the dealflow has items but filters matched nothing */
  isFilterEmpty: boolean;
  /** Total number of deals in the unfiltered dealflow */
  totalDeals: number;
  /** Whether a search query is active */
  hasQuery: boolean;
  /** Number of active filters */
  activeFilterCount: number;
  onOpenAnalysis: () => void;
  onClearSearch: () => void;
  onResetFilters: () => void;
  onDiscoverStartups: () => void;
  onBrowseDirectory: () => void;
}

export function DealflowEmptyState({
  isFilterEmpty,
  totalDeals,
  hasQuery,
  activeFilterCount,
  onOpenAnalysis,
  onClearSearch,
  onResetFilters,
  onDiscoverStartups,
  onBrowseDirectory,
}: DealflowEmptyStateProps) {
  const colors = useThemeColors();

  // Filters returned no results
  if (isFilterEmpty) {
    return (
      <Animated.View
        entering={FadeIn.delay(100).duration(300)}
        className="items-center py-8 px-4 w-full"
      >
        <View
          className="w-16 h-16 rounded-full items-center justify-center mb-3"
          style={{ backgroundColor: colors.surface.secondary }}
        >
          <Eye size={28} color={colors.text.tertiary} />
        </View>
        <Text style={{ color: colors.text.primary }} className="font-semibold text-center mb-1">
          No matches found
        </Text>
        <Text style={{ color: colors.text.tertiary }} className="text-center text-sm mb-4">
          Try adjusting your search or filters
        </Text>

        {/* Suggestions */}
        <View className="flex-row flex-wrap justify-center gap-2">
          {hasQuery && (
            <AnimatedPressable
              onPress={onClearSearch}
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              className="flex-row items-center gap-1.5 px-3 py-2 rounded-lg"
              style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
            >
              <X size={14} color={colors.text.secondary} />
              <Text style={{ color: colors.text.secondary }} className="text-sm">Clear search</Text>
            </AnimatedPressable>
          )}
          {activeFilterCount > 0 && (
            <AnimatedPressable
              onPress={onResetFilters}
              accessibilityRole="button"
              accessibilityLabel="Reset all filters"
              className="flex-row items-center gap-1.5 px-3 py-2 rounded-lg"
              style={{ backgroundColor: colors.primary[50], borderWidth: 1, borderColor: colors.primary[200] }}
            >
              <RotateCcw size={14} color={colors.primary[600]} />
              <Text style={{ color: colors.primary[600] }} className="text-sm">Reset filters</Text>
            </AnimatedPressable>
          )}
        </View>

        <Text style={{ color: colors.text.tertiary }} className="text-xs mt-4">
          {totalDeals} startup{totalDeals !== 1 ? 's' : ''} in your dealflow
        </Text>
      </Animated.View>
    );
  }

  // No startups in dealflow at all
  return (
    <Animated.View
      entering={FadeIn.delay(100).duration(400)}
      className="items-center py-8 px-4 w-full"
    >
      <View
        className="w-20 h-20 rounded-full items-center justify-center mb-4"
        style={{ backgroundColor: colors.primary[100] }}
      >
        <Sparkles size={40} color={colors.primary[600]} />
      </View>
      <Text style={{ color: colors.text.primary }} className="text-xl font-bold text-center mb-2">
        Start Building Your Dealflow
      </Text>
      <Text style={{ color: colors.text.secondary }} className="text-center mb-6 leading-5">
        Your dealflow is where you track and evaluate{'\n'}
        potential investment opportunities.
      </Text>

      {/* How it works */}
      <View
        className="w-full rounded-xl p-4 mb-6"
        style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
      >
        <Text style={{ color: colors.text.primary }} className="font-semibold mb-3">
          How it works
        </Text>
        <View className="gap-3">
          {[
            { num: '1', title: 'Upload a pitch deck', subtitle: 'PDF or PowerPoint files supported' },
            { num: '2', title: 'AI extracts key data', subtitle: 'Team, market, traction, financials' },
            { num: '3', title: 'Get an investment readiness score', subtitle: 'Based on 14 due diligence dimensions' },
          ].map((step) => (
            <View key={step.num} className="flex-row items-start gap-3">
              <View
                className="w-7 h-7 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.primary[100] }}
              >
                <Text style={{ color: colors.primary[600] }} className="font-bold text-sm">{step.num}</Text>
              </View>
              <View className="flex-1">
                <Text style={{ color: colors.text.primary }} className="font-medium text-sm">{step.title}</Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">{step.subtitle}</Text>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* CTA Button */}
      <AnimatedPressable
        onPress={onOpenAnalysis}
        accessibilityRole="button"
        accessibilityLabel="Analyze your first startup"
        className="flex-row items-center gap-2 px-6 py-4 rounded-xl"
        style={{ backgroundColor: colors.primary[600] }}
      >
        <Sparkles size={20} color="white" />
        <Text className="text-white font-semibold">Analyze Your First Startup</Text>
      </AnimatedPressable>

      {/* Secondary actions */}
      <AnimatedPressable
        onPress={onDiscoverStartups}
        accessibilityRole="button"
        accessibilityLabel="Browse startups on the platform"
        className="flex-row items-center gap-2 mt-4 px-4 py-2"
      >
        <Compass size={16} color={colors.primary[600]} />
        <Text style={{ color: colors.primary[600] }} className="font-medium">
          Or browse startups on the platform
        </Text>
      </AnimatedPressable>
      <AnimatedPressable
        onPress={onBrowseDirectory}
        accessibilityRole="button"
        accessibilityLabel="Browse startup directory"
        className="flex-row items-center gap-2 mt-2 px-4 py-2"
      >
        <MapPin size={16} color={colors.text.secondary} />
        <Text style={{ color: colors.text.secondary }} className="font-medium">
          Browse Startup Directory
        </Text>
      </AnimatedPressable>
    </Animated.View>
  );
}
